import { useEffect, useId, useRef } from "react";
import { Button, ButtonGroup, Card, Form } from "react-bootstrap";
import type { DateRange, DateRangePreset } from "@/types/analytics";

export type DateRangePresetOption = {
  key: DateRangePreset;
  label: string;
  disabled?: boolean;
  /** Visible text saying why the preset is disabled. */
  reason?: string | null;
};

// There is no all-time preset (ANL-E04-S05): it read every day since 2020 with no server cap (AP-18).
const DEFAULT_PRESETS: readonly DateRangePresetOption[] = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "7d" },
  { key: "30d", label: "30d" },
  { key: "90d", label: "90d" },
  { key: "custom", label: "Custom" },
];

const formatDate = (value: Date) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const buildPresetRange = (
  preset: Exclude<DateRangePreset, "custom">
): DateRange => {
  const today = new Date();
  const end = formatDate(today);

  if (preset === "today") {
    return { preset, start: end, end };
  }

  if (preset === "yesterday" || preset === "24h") {
    const yesterday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - 1
    );
    const start = formatDate(yesterday);
    return { preset, start, end: preset === "24h" ? end : start };
  }

  const daysBack = preset === "7d" ? 6 : preset === "30d" ? 29 : 89;
  const startDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - daysBack
  );

  return { preset, start: formatDate(startDate), end };
};

type DateRangeSelectorProps = {
  value: DateRange;
  onChange: (nextRange: DateRange) => void;
  className?: string;
  variant?: "card" | "inline";
  /** The presets on offer, in order; analytics views pass their own set (ANL-E04-S05). */
  presets?: readonly DateRangePresetOption[];
  /** Turns a preset into its dates; analytics views resolve in UTC. */
  resolvePreset?: (preset: Exclude<DateRangePreset, "custom">) => DateRange;
};

const DateRangeSelector = ({
  value,
  onChange,
  className = "mb-3",
  variant = "card",
  presets = DEFAULT_PRESETS,
  resolvePreset = buildPresetRange,
}: DateRangeSelectorProps) => {
  const reasonIdPrefix = useId();
  const latestValue = useRef(value);

  useEffect(() => {
    latestValue.current = value;
  }, [value]);

  const handlePreset = (preset: DateRangePreset) => {
    if (preset === "custom") {
      const nextRange: DateRange = { ...latestValue.current, preset: "custom" };
      latestValue.current = nextRange;
      onChange(nextRange);
      return;
    }
    const nextRange = resolvePreset(preset);
    latestValue.current = nextRange;
    onChange(nextRange);
  };
  const reasonId = (preset: DateRangePreset) => `${reasonIdPrefix}-${preset}-reason`;
  const disabledWithReason = presets.filter((preset) => preset.disabled && preset.reason);

  const handleCustomChange = (field: "start" | "end", next: string) => {
    if (!next) return;
    const currentValue = latestValue.current;
    const updated: DateRange = {
      ...currentValue,
      preset: "custom",
      start: field === "start" ? next : currentValue.start,
      end: field === "end" ? next : currentValue.end,
    };
    if (updated.start > updated.end) {
      const nextRange = { ...updated, end: updated.start };
      latestValue.current = nextRange;
      onChange(nextRange);
      return;
    }
    latestValue.current = updated;
    onChange(updated);
  };

  const customControlsClassName =
    variant === "inline"
      ? "d-flex flex-row flex-nowrap align-items-center gap-2"
      : "d-flex flex-wrap align-items-center gap-2";

  const controls = (
    <div className="d-flex flex-wrap align-items-center gap-2">
      <ButtonGroup>
        {presets.map((preset) => (
          <Button
            key={preset.key}
            size="sm"
            variant={value.preset === preset.key ? "primary" : "outline-primary"}
            aria-pressed={value.preset === preset.key}
            disabled={preset.disabled}
            aria-describedby={preset.disabled && preset.reason ? reasonId(preset.key) : undefined}
            onClick={() => handlePreset(preset.key)}
          >
            {preset.label}
          </Button>
        ))}
      </ButtonGroup>
      {disabledWithReason.map((preset) => (
        <span key={preset.key} id={reasonId(preset.key)} className="text-muted small">
          {preset.reason}
        </span>
      ))}
      {value.preset === "custom" ? (
        <div className={customControlsClassName}>
          <Form.Control
            type="date"
            aria-label="Start date"
            size="sm"
            value={value.start}
            max={value.end}
            onChange={(event) =>
              handleCustomChange("start", event.target.value)
            }
          />
          <span className="text-muted small">to</span>
          <Form.Control
            type="date"
            aria-label="End date"
            size="sm"
            value={value.end}
            min={value.start}
            onChange={(event) => handleCustomChange("end", event.target.value)}
          />
        </div>
      ) : null}
    </div>
  );

  if (variant === "inline") {
    return <div className={className}>{controls}</div>;
  }

  return (
    <Card className={className}>
      <Card.Body className="py-2">{controls}</Card.Body>
    </Card>
  );
};

export default DateRangeSelector;
